/**
 * Coffee Lingo page — interactivity for sections/main-coffee-lingo.liquid.
 * Progressive enhancement: the page reads fine without any of this.
 *
 * Perf notes (same rules as the flavour wheel):
 * - Shopify's storefront autoloader runs a childList MutationObserver on
 *   document.body, so high-frequency updates (slider drags) mutate existing
 *   text nodes via node.data (characterData — ignored by the observer) and
 *   CSS custom properties, never insert/remove nodes.
 * - No :has() selectors; state is explicit classes/attributes toggled here.
 */

(() => {
  const root = document.querySelector('[data-coffee-lingo]');
  if (!root) return;
  root.setAttribute('data-lingo-ready', '1'); // module-failure watchdog stands down

  const $ = (sel, scope = root) => scope.querySelector(sel);
  const $$ = (sel, scope = root) => Array.from(scope.querySelectorAll(sel));

  /** Ensure an element has a single permanent text node and return it. */
  const textNode = (el) => {
    if (!(el.firstChild instanceof Text) || el.childNodes.length !== 1) {
      el.replaceChildren(document.createTextNode(''));
    }
    return el.firstChild;
  };

  const setPressed = (buttons, active) => {
    for (const b of buttons) {
      const on = b === active;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  };

  /* ================= sound: a small coffee-themed synth =================
     This page has its own voice (the flavour wheel keeps its pentatonic):
     crystal-glass tings for the wine picker, first-crack pops on the roast
     slider, steam-wand hiss for milk, woody plucks for chips, marimba for
     scores, and a café counter bell when a lesson lands. All synthesized,
     lazy AudioContext on first gesture, shared compressor bus. */
  const SOUND_KEY = 'ccc-lingo-sound';
  let soundOn = true;
  try { soundOn = localStorage.getItem(SOUND_KEY) !== 'off'; } catch { /* storage blocked */ }
  let audioCtx = null;
  let masterBus = null;
  const audio = () => {
    if (!soundOn) return null;
    if (!audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtx = new Ctx();
    }
    if (audioCtx.state === 'suspended') audioCtx.resume();
    return audioCtx;
  };
  const master = () => {
    const ctx = audio();
    if (!ctx) return null;
    if (!masterBus) {
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -20;
      comp.knee.value = 26;
      comp.ratio.value = 12;
      comp.attack.value = 0.003;
      comp.release.value = 0.2;
      const g = ctx.createGain();
      g.gain.value = 0.8;
      comp.connect(g).connect(ctx.destination);
      masterBus = comp;
    }
    return masterBus;
  };
  /** Woody pluck — chip taps. A triangle hit through a snappy lowpass. */
  const pluck = (freq = 600, gain = 0.05, dur = 0.14) => {
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.82, t + dur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.Q.value = 3;
    lp.frequency.setValueAtTime(freq * 4, t);
    lp.frequency.exponentialRampToValueAtTime(freq * 1.2, t + dur);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(lp);
    lp.connect(amp);
    amp.connect(master());
    osc.start(t);
    osc.stop(t + dur + 0.03);
  };
  /** Crystal wine-glass ting — inharmonic partials with a long shimmer. */
  const ting = (freq = 1318, gain = 0.035) => {
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(gain, t + 0.008);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
    for (const [mult, g0, det] of [[1, 1, 0], [2.32, 0.38, 3], [4.25, 0.14, -4]]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq * mult;
      osc.detune.value = det;
      const g = ctx.createGain();
      g.gain.value = g0;
      osc.connect(g);
      g.connect(amp);
      osc.start(t);
      osc.stop(t + 1.15);
    }
    amp.connect(master());
  };
  /** Marimba note — the judge's scores and sliders. */
  const marimba = (freq = 440, gain = 0.05) => {
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(gain, t + 0.005);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    for (const [mult, g0] of [[1, 1], [3.9, 0.25], [9.2, 0.08]]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq * mult;
      const g = ctx.createGain();
      g.gain.value = g0;
      osc.connect(g);
      g.connect(amp);
      osc.start(t);
      osc.stop(t + 0.45);
    }
    amp.connect(master());
  };
  /** First-crack pops — a scatter of tiny highpassed noise snaps. */
  const crackle = (pops = 5) => {
    const ctx = audio();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    for (let i = 0; i < pops; i++) {
      const t = t0 + Math.random() * 0.28;
      const frames = Math.floor(ctx.sampleRate * 0.02);
      const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let j = 0; j < frames; j++) data[j] = (Math.random() * 2 - 1) * (1 - j / frames) ** 2;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 1400 + Math.random() * 2000;
      const g = ctx.createGain();
      g.gain.value = 0.1 + Math.random() * 0.08;
      src.connect(hp);
      hp.connect(g);
      g.connect(master());
      src.start(t);
    }
  };
  /** Steam-wand hiss — pitch rides the milk temperature. */
  const steam = (level = 0.5, dur = 0.22) => {
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const frames = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let j = 0; j < frames; j++) data[j] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.1;
    bp.frequency.setValueAtTime(900 + level * 3200, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.03 + level * 0.02, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp);
    bp.connect(g);
    g.connect(master());
    src.start(t);
  };
  /** Card swish — glossary flips. */
  const swish = () => steam(0.2, 0.13);
  /** Café counter bell — a lesson lands. Ding! */
  const bell = (double = false) => {
    const ctx = audio();
    if (!ctx) return;
    const strike = (t, gain) => {
      const amp = ctx.createGain();
      amp.gain.setValueAtTime(0.0001, t);
      amp.gain.exponentialRampToValueAtTime(gain, t + 0.006);
      amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
      for (const [mult, g0] of [[1, 1], [2.71, 0.42], [4.87, 0.18], [7.2, 0.07]]) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = 1244 * mult;
        const g = ctx.createGain();
        g.gain.value = g0;
        osc.connect(g);
        g.connect(amp);
        osc.start(t);
        osc.stop(t + 0.95);
      }
      amp.connect(master());
    };
    const t = ctx.currentTime;
    strike(t, 0.05);
    if (double) strike(t + 0.16, 0.045);
  };
  /** Graduation flourish — rising tings into the bell. */
  const fanfare = () => {
    if (!audio()) return;
    ting(1046);
    setTimeout(() => ting(1318), 130);
    setTimeout(() => ting(1568), 260);
    setTimeout(() => bell(true), 420);
  };
  const tick = () => pluck(620);
  const flick = () => pluck(900, 0.04, 0.1);
  const CHIME_TINGS = [1046, 1318, 1568]; // C6-E6-G6 — this page's crystal triad
  let chimeIx = 0;
  const chime = (freq) => ting(freq || CHIME_TINGS[chimeIx++ % CHIME_TINGS.length]);
  const blip = (freq = 520, gain = 0.04) => marimba(freq, gain);

  /* ---- playful one-shots for the tappable hero words ---- */
  const splash = () => { // "washed" — a little water
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const frames = Math.floor(ctx.sampleRate * 0.35);
    const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
    const dd = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) dd[i] = (Math.random() * 2 - 1) * (1 - i / frames);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(3600, t);
    lp.frequency.exponentialRampToValueAtTime(300, t + 0.35);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.09, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    src.connect(lp); lp.connect(g); g.connect(master());
    src.start(t);
    setTimeout(() => blip(500, 0.03), 130);
  };
  const sweepUp = () => { // "dialling in" — a rising dial
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(240, t);
    osc.frequency.exponentialRampToValueAtTime(920, t + 0.28);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
    osc.connect(g); g.connect(master());
    osc.start(t); osc.stop(t + 0.35);
  };
  const gliss = () => { // "silky finish" — a smooth downward glide
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(320, t + 0.5);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
    osc.connect(g); g.connect(master());
    osc.start(t); osc.stop(t + 0.6);
  };
  const boing = () => { // "mouthfeel" — rubbery wobble
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(520, t + 0.08);
    osc.frequency.exponentialRampToValueAtTime(230, t + 0.28);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.06, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    osc.connect(g); g.connect(master());
    osc.start(t); osc.stop(t + 0.32);
  };
  const funky = () => { // "natural" — a wild little riff
    const ctx = audio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(160, t);
    osc.frequency.setValueAtTime(214, t + 0.09);
    osc.frequency.setValueAtTime(150, t + 0.18);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 720; lp.Q.value = 7;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
    osc.connect(lp); lp.connect(g); g.connect(master());
    osc.start(t); osc.stop(t + 0.3);
  };
  const godshot = () => { bell(true); setTimeout(() => ting(1568), 120); setTimeout(() => ting(2093), 250); };
  const foam = () => { steam(0.4, 0.3); setTimeout(() => ting(1760, 0.03), 80); };

  // Word → sound. Drop a licensed/own voice recording in later by adding
  // data-clip="{{ 'washed.mp3' | asset_url }}" to a hero word and it plays that.
  const HERO_SOUNDS = {
    crema: foam,
    terroir: () => marimba(140, 0.06),
    'god shot': godshot,
    mouthfeel: boing,
    'first crack': () => crackle(6),
    washed: splash,
    peaberry: () => blip(1046, 0.045),
    'dialling in': sweepUp,
    natural: funky,
    'silky finish': gliss,
  };

  /* ================= confetti (celebrations) ================= */
  const confetti = () => {
    if (reduceMotion) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'lingo-confetti';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const c2d = canvas.getContext('2d');
    const COLORS = ['#b88746', '#7c2d3f', '#55745a', '#e8d7be', '#6e4528', '#e2725b'];
    const parts = Array.from({ length: 110 }, () => ({
      x: canvas.width / 2 + (Math.random() - 0.5) * canvas.width * 0.5,
      y: canvas.height * 0.35 + (Math.random() - 0.5) * 80,
      vx: (Math.random() - 0.5) * 9,
      vy: -(4 + Math.random() * 8),
      w: 5 + Math.random() * 6,
      h: 3 + Math.random() * 5,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      c: COLORS[(Math.random() * COLORS.length) | 0],
    }));
    const t0 = performance.now();
    const step = (now) => {
      c2d.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of parts) {
        p.vy += 0.25;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        c2d.save();
        c2d.translate(p.x, p.y);
        c2d.rotate(p.rot);
        c2d.fillStyle = p.c;
        c2d.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        c2d.restore();
      }
      if (now - t0 < 2400) requestAnimationFrame(step);
      else canvas.remove();
    };
    requestAnimationFrame(step);
  };

  const soundBtn = $('[data-lingo-sound]');
  if (soundBtn) {
    const iconNode = textNode(soundBtn);
    const paint = () => {
      soundBtn.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
      iconNode.data = soundOn ? '\u{1F50A}' : '\u{1F507}';
    };
    paint();
    soundBtn.addEventListener('click', () => {
      soundBtn.classList.remove('is-popped');
      void soundBtn.offsetWidth;
      soundBtn.classList.add('is-popped');
      soundOn = !soundOn;
      try { localStorage.setItem(SOUND_KEY, soundOn ? 'on' : 'off'); } catch { /* ignore */ }
      paint();
      if (soundOn) chime();
    });
  }

  /* ================= reveal on scroll ================= */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const candidates = $$('.lingo-band .lingo-card, .lingo-band .lingo-step, .lingo-cheat__row, .lingo-wine__panels, .lingo-tree, .lingo-gcard');
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }
    }, { rootMargin: '0px 0px -8% 0px' });
    for (const el of candidates) {
      // Only hide elements that are still below the fold; anything already
      // visible (or if IO never fires) stays readable.
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.classList.add('lingo-reveal');
        io.observe(el);
      }
    }
  }

  /* ================= coffee school: one lesson at a time =================
     Lessons ship server-rendered with [hidden] (noscript CSS reveals them all
     when JS is off); this controller shows one at a time and tracks progress. */
  const LESSONS = ['the-fruit', 'wine', 'varieties', 'label', 'lingo', 'taste', 'roast', 'milk', 'water', 'dialin', 'faults', 'home', 'quiz'];
  const TITLES = {
    'the-fruit': 'The fruit', wine: 'The wine thing', varieties: 'Varieties', label: 'Reading the label', lingo: 'The lingo',
    taste: 'Taste like a judge', roast: 'Roast myths', milk: 'Milk school', water: 'Water', dialin: 'Dial-in lab',
    faults: 'Is it off?', home: 'Every coffee has a home', quiz: 'Find your coffee', dictionary: 'Glossary',
  };
  const sections = new Map($$('[data-lingo-chapter]').map((s) => [s.dataset.lingoChapter, s]));
  const navLinks = new Map($$('[data-lingo-navlink]').map((a) => [a.dataset.lingoNavlink, a]));
  const hubCards = new Map($$('[data-hub-lesson]').map((b) => [b.dataset.hubLesson, b]));
  const outro = $('[data-lingo-outro]');
  const navEl = $('[data-lingo-nav]');
  const navTrack = navEl ? $('.lingo-nav__track', navEl) : null;
  const updateNavOverflow = () => {
    if (!navEl || !navTrack) return;
    const overflowing = navTrack.scrollWidth > navTrack.clientWidth + 4;
    navEl.classList.toggle('has-overflow', overflowing);
    // positive class = opt IN to centring, so the pre-JS default stays flex-start
    // (no centred-then-left snap on the deferred module's boot at laptop widths)
    navEl.classList.toggle('nav-fits', !overflowing);
  };
  updateNavOverflow();
  window.addEventListener('resize', updateNavOverflow);
  if (window.ResizeObserver && navTrack) {
    new ResizeObserver(updateNavOverflow).observe(navTrack);
  }
  const hubFill = $('[data-hub-fill]');
  const hubCountEl = $('[data-hub-count]');
  const hubCountNode = hubCountEl ? textNode(hubCountEl) : null;
  const hubGrad = $('[data-hub-grad]');
  const hubReset = $('[data-hub-reset]');
  let resetQuizForm = null; // assigned inside the personality-quiz block; called on untick/reset
  // Progress saves per-account when signed in (so shared computers don't mix
  // up), otherwise per-device. True cross-device sync would need a backend.
  const custId = root.dataset.customerId || '';
  const custName = root.dataset.customerName || '';
  const PROGRESS_KEY = custId ? `ccc-lingo-progress-${custId}` : 'ccc-lingo-progress';
  let doneLessons = new Set();
  try { doneLessons = new Set(JSON.parse(localStorage.getItem(PROGRESS_KEY) || '[]')); } catch { /* ignore */ }
  let currentLesson = null;

  const saveNote = $('[data-lingo-savenote]');
  if (saveNote) {
    saveNote.hidden = false;
    saveNote.innerHTML = custId
      ? `Signed in${custName ? ` as ${custName}` : ''}, your progress &amp; diploma are saved. \u{1F516}`
      : 'Your progress saves on this device. <a href="/account/login">Log in</a> to tie it to your account.';
  }

  // sticky "back to your lesson" bar — so browsing coffee never loses your place
  const progressbar = $('[data-lingo-progressbar]');
  const progNum = progressbar && textNode($('[data-progress-num]', progressbar));
  const progLabel = progressbar && textNode($('[data-progress-label]', progressbar));
  const progTitle = progressbar && textNode($('[data-progress-title]', progressbar));
  const updateBar = () => {
    if (!currentLesson) return;
    const idx = LESSONS.indexOf(currentLesson);
    const n = idx + 1;
    if (progressbar) {
      if (idx === -1) { // a reference tab (e.g. the glossary), not a numbered lesson
        progNum.data = '\u{1F4D6}';
        progLabel.data = TITLES[currentLesson] || 'Glossary';
        progTitle.data = '';
        const refBtn = $('[data-progress-resume]', progressbar);
        if (refBtn) refBtn.removeAttribute('aria-label'); // fall back to the visible "Glossary" text
      } else {
        const compact = window.matchMedia('(max-width: 560px)').matches;
        progNum.data = String(n);
        progLabel.data = compact ? `${n}/${LESSONS.length}` : `Lesson ${n} of ${LESSONS.length}`;
        progTitle.data = TITLES[currentLesson] || ''; // hidden by CSS on phones, kept for aria
        const resumeBtn = $('[data-progress-resume]', progressbar);
        if (resumeBtn) {
          resumeBtn.setAttribute('aria-label', `Lesson ${n} of ${LESSONS.length}: ${TITLES[currentLesson] || ''}, back to your lesson`);
        }
      }
    }
    if (idx === -1) {
      // reference tab: don't write a lesson breadcrumb, but if progress was just
      // reset to zero, still clear any stale crumb (S-15 on the glossary path).
      if (doneLessons.size === 0) {
        try { localStorage.removeItem('ccc-lingo-resume'); } catch { /* ignore */ }
      }
      return;
    }
    // leave a breadcrumb for the site-wide "resume" pill on other store pages
    try {
      if (doneLessons.size > 0) {
        const crumb = JSON.stringify({
          key: currentLesson,
          num: n,
          total: LESSONS.length,
          title: TITLES[currentLesson] || '',
          done: doneLessons.size,
          complete: doneLessons.size >= LESSONS.length,
        });
        // A dismissed pill stays dismissed until the shopper re-engages with
        // the course. A changed crumb (reached a new lesson, completed one)
        // is that signal — a plain revisit rewrites the same crumb and keeps
        // the dismissal.
        if (localStorage.getItem('ccc-lingo-resume') !== crumb) {
          localStorage.removeItem('ccc-lingo-resume-x');
        }
        localStorage.setItem('ccc-lingo-resume', crumb);
      } else {
        // no progress yet (fresh landing or post-reset) -> no cross-page resume nag
        localStorage.removeItem('ccc-lingo-resume');
      }
    } catch { /* ignore */ }
  };
  if (progressbar) {
    $('[data-progress-resume]', progressbar).addEventListener('click', () => {
      const sec = sections.get(currentLesson);
      if (sec) sec.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
    // reveal the bar only once the hub has scrolled out of view
    const hub = $('[data-lingo-hub]');
    if (hub && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        for (const e of entries) {
          progressbar.hidden = false;
          progressbar.classList.toggle('is-shown', !e.isIntersecting);
          if (!e.isIntersecting && progressbar._armDim) progressbar._armDim();
        }
      }, { rootMargin: '-40% 0px 0px 0px' }).observe(hub);
    }
    // auto-dim the pill after a reading pause so it stops sitting on the text;
    // restore on scroll or tap. Skipped entirely under reduced motion.
    if (!reduceMotion) {
      let dimTimer;
      const armDim = () => {
        clearTimeout(dimTimer);
        progressbar.classList.remove('is-dim');
        dimTimer = setTimeout(() => progressbar.classList.add('is-dim'), 2500);
      };
      // capture so a scroll on the theme's custom scroll container also re-arms
      window.addEventListener('scroll', armDim, { passive: true, capture: true });
      progressbar.addEventListener('pointerdown', armDim);
      progressbar._armDim = armDim;
    }
    // keep the label format (compact "3/13" vs full) in sync on rotate/resize
    window.matchMedia('(max-width: 560px)').addEventListener?.('change', updateBar);
  }

  const paintHub = () => {
    for (const [key, card] of hubCards) {
      card.classList.toggle('is-done', doneLessons.has(key));
      card.classList.toggle('is-active', key === currentLesson);
      if (key === currentLesson) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
    }
    if (hubFill) hubFill.style.setProperty('width', `${(doneLessons.size / LESSONS.length) * 100}%`);
    if (hubCountNode) hubCountNode.data = `${doneLessons.size} of ${LESSONS.length} done`;
    if (hubGrad) hubGrad.hidden = doneLessons.size < LESSONS.length;
    if (hubReset) hubReset.hidden = doneLessons.size === 0;
  };

  const showLesson = (key, opts = {}) => {
    if (!sections.has(key)) return;
    currentLesson = key;
    for (const [k, s] of sections) s.hidden = k !== key;
    // the victory-lap outro only appears once the quiz has actually been finished
    if (outro) outro.hidden = !(key === 'quiz' && doneLessons.has('quiz'));
    if (!opts.silentSound) swish();
    for (const [k, a] of navLinks) {
      a.classList.toggle('is-current', k === key);
      if (k === key) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    }
    // keep the current pill visible inside the scrollable nav track
    const pill = navLinks.get(key);
    if (pill && navTrack) {
      navTrack.scrollTo({
        left: pill.offsetLeft - navTrack.clientWidth / 2 + pill.offsetWidth / 2,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
    updateBar();
    // page-turn: replay the entrance animation on the freshly shown lesson
    const section = sections.get(key);
    section.classList.remove('is-entering');
    void section.offsetWidth;
    section.classList.add('is-entering');
    setTimeout(() => section.classList.remove('is-entering'), 1100);
    paintHub();
    renderExam(key);
    if (opts.scroll !== false) {
      sections.get(key).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
    // Don't write the hash on the initial default show — the theme has its own
    // scroll-to-hash-on-load behaviour and would yank the page past the hero.
    if (opts.updateUrl !== false && history.replaceState) history.replaceState(null, '', `#${key}`);
  };

  const markDone = (key, opts = {}) => {
    if (!doneLessons.has(key)) {
      doneLessons.add(key);
      try { localStorage.setItem(PROGRESS_KEY, JSON.stringify([...doneLessons])); } catch { /* ignore */ }
      if (!opts.silent) {
        if (doneLessons.size === LESSONS.length) {
          fanfare();
          confetti();
        } else {
          bell();
        }
      }
      const card = hubCards.get(key);
      if (card) {
        card.classList.add('was-just-done');
        setTimeout(() => card.classList.remove('was-just-done'), 600);
      }
    }
    paintHub();
    updateBar(); // refresh the cross-page resume breadcrumb (done count / complete)
  };

  const unmarkDone = (key) => {
    if (!doneLessons.has(key)) return;
    doneLessons.delete(key);
    try { localStorage.setItem(PROGRESS_KEY, JSON.stringify([...doneLessons])); } catch { /* ignore */ }
    if (key === 'quiz') { // the personality quiz has no section exam, so reset its own DOM
      if (outro) outro.hidden = true; // pull the diploma victory-lap back
      if (resetQuizForm) resetQuizForm();
    }
    paintHub();
    updateBar();
    if (currentLesson === key) renderExam(key); // if it's on screen, re-arm its quick check
    const card = hubCards.get(key); // keep keyboard/SR focus on the card, don't drop to <body>
    if (card) card.focus({ preventScroll: true });
    tick();
  };

  /* ============ section exams — pass at 3, then finish the 12-question set if you like ============ */
  const QUIZBANK = (() => {
    const island = document.querySelector('[data-lingo-quizbank]'); // sits outside root
    try { return island ? JSON.parse(island.textContent) || {} : {}; } catch { return {}; }
  })();
  const PASS_NEEDED = 3;
  const EXAM_POOL = 12; // show the whole pool each attempt; a pass still lands at 3 right
  const seenByKey = new Map(); // question indices already served, so keep-going stays fresh
  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };
  const examEls = new Map();
  for (const exam of $$('[data-lingo-exam]')) {
    const key = exam.dataset.lingoExam;
    const section = sections.get(key);
    examEls.set(key, {
      exam,
      stage: $('[data-exam-stage]', exam),
      dots: $('[data-exam-dots]', exam),
      result: $('[data-exam-result]', exam),
      doneBtn: section ? section.querySelector('[data-lesson-done]') : null,
    });
  }

  const clearPassline = (el) => { const pl = el.exam.querySelector('.lingo-exam__passline'); if (pl) pl.remove(); };

  function renderExam(key) {
    const el = examEls.get(key);
    if (!el) return;
    const pool = QUIZBANK[key];
    if (!Array.isArray(pool) || pool.length < PASS_NEEDED) {
      el.exam.hidden = true;
      if (el.doneBtn) el.doneBtn.hidden = false; // no questions → don't gate
      return;
    }
    el.exam.hidden = false;
    if (doneLessons.has(key)) { renderPassed(key); return; }
    if (el.doneBtn) el.doneBtn.hidden = true;
    startExam(key);
  }

  const addBtn = (parent, label, kind, fn) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `lingo-btn lingo-btn--${kind}`;
    b.textContent = label;
    b.addEventListener('click', fn);
    parent.appendChild(b);
    return b;
  };

  // primary "move on" button for a pass/result card — reuses the lesson-foot Done handler
  const addContinue = (el, key) => {
    if (!el.doneBtn) return;
    const next = LESSONS[LESSONS.indexOf(key) + 1];
    return addBtn(el.result, next ? `Continue → ${TITLES[next]}` : 'Continue →', 'brass', () => { tick(); el.doneBtn.click(); });
  };

  // practice=false → graded run; practice=true → for-fun rerun. Either way, 3 right ends the round.
  function runQuiz(key, practice) {
    const el = examEls.get(key);
    const pool = QUIZBANK[key];
    const roundSize = Math.min(EXAM_POOL, pool.length);
    let seen = seenByKey.get(key);
    if (!seen) { seen = new Set(); seenByKey.set(key, seen); }
    let avail = [...pool.keys()].filter((i) => !seen.has(i));
    if (avail.length < roundSize) { seen.clear(); avail = [...pool.keys()]; } // seen them all, cycle round
    const order = shuffle(avail).slice(0, roundSize);
    let idx = 0, correct = 0, asked = 0, passed = false;
    el.result.hidden = true;
    el.stage.hidden = false;
    clearPassline(el);
    el.dots.replaceChildren();
    const dotEls = order.map(() => {
      const d = document.createElement('span');
      d.className = 'lingo-exam__dot';
      el.dots.appendChild(d);
      return d;
    });
    const ask = () => {
      const q = pool[order[idx]];
      const qp = document.createElement('p');
      qp.className = 'lingo-exam__q';
      qp.textContent = q.q;
      const opts = document.createElement('div');
      opts.className = 'lingo-exam__options';
      q.a.forEach((label, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lingo-chip lingo-exam__opt';
        b.textContent = label;
        b.addEventListener('click', () => choose(b, i, q, opts));
        opts.appendChild(b);
      });
      el.stage.replaceChildren(qp, opts);
    };
    const choose = (btn, i, q, opts) => {
      for (const c of opts.children) c.disabled = true;
      const right = i === q.correct;
      if (right) {
        btn.classList.add('is-right');
        correct += 1;
        chime();
        dotEls[asked].classList.add('is-right');
      } else {
        btn.classList.add('is-wrong');
        opts.children[q.correct].classList.add('is-right');
        pluck(200, 0.05, 0.2);
        dotEls[asked].classList.add('is-wrong');
      }
      seen.add(order[idx]);
      asked += 1;
      idx += 1;
      if (!passed && correct >= PASS_NEEDED) { passed = true; revealPass(el, key, practice); }
      const delay = reduceMotion ? 450 : (right ? 720 : 1150);
      setTimeout(() => {
        if (asked >= order.length) {
          if (practice) finishPractice(key, correct, order.length);
          else if (passed) passExam(key, correct, order.length);
          else failExam(key);
        } else ask();
      }, delay);
    };
    ask();
  }
  const startExam = (key) => runQuiz(key, false);
  const startPractice = (key) => runQuiz(key, true);

  // The moment they hit 3 right: bank the pass, but let the set keep running.
  function revealPass(el, key, practice) {
    chime();
    setTimeout(() => chime(1568), 130);
    if (!practice) markDone(key); // hub tick + reward now, even if they carry on
    if (el.exam.querySelector('.lingo-exam__passline')) return;
    const line = document.createElement('div');
    line.className = 'lingo-exam__passline';
    const span = document.createElement('span');
    span.textContent = practice
      ? '✅ Three for three, still sharp. Play the rest for fun, or move on.'
      : '✅ Passed! Three right. Keep going through the set, or move on whenever.';
    line.appendChild(span);
    if (!practice && el.doneBtn) {
      const mv = document.createElement('button');
      mv.type = 'button';
      mv.className = 'lingo-btn lingo-btn--brass lingo-exam__passmove';
      mv.textContent = 'Move on →';
      mv.addEventListener('click', () => { tick(); el.doneBtn.click(); });
      line.appendChild(mv);
    }
    el.exam.insertBefore(line, el.stage);
  }

  function passExam(key, correct, total) {
    const el = examEls.get(key);
    el.stage.hidden = true;
    clearPassline(el);
    el.result.hidden = false;
    el.result.className = 'lingo-exam__result lingo-exam__result--pass';
    const full = correct === total;
    el.result.innerHTML = `<h4>\u{1F389} ${full ? 'Full marks!' : 'Passed!'}</h4><p>${correct} of ${total} right, and this one’s in the bag.</p>`;
    chime();
    setTimeout(() => chime(1568), 130);
    if (el.doneBtn) el.doneBtn.hidden = true; // the pass card's Continue is the one visible CTA
    const primary = addContinue(el, key); // clear way on to the next lesson
    addBtn(el.result, 'Go again ↻', 'ghost', () => { tick(); startPractice(key); });
    if (primary) primary.focus({ preventScroll: true });
  }

  function finishPractice(key, correct, total) {
    const el = examEls.get(key);
    el.stage.hidden = true;
    clearPassline(el);
    el.result.hidden = false;
    el.result.className = 'lingo-exam__result lingo-exam__result--pass';
    const full = correct === total;
    el.result.innerHTML = `<h4>${full ? '🎯 Full marks!' : `Nice, ${correct} out of ${total}!`}</h4><p>Just for fun, this one’s already in the bag.</p>`;
    if (full) { chime(); setTimeout(() => chime(1568), 120); setTimeout(() => chime(1976), 240); } else chime();
    if (el.doneBtn) el.doneBtn.hidden = true;
    const primary = addContinue(el, key);
    addBtn(el.result, 'Go again ↻', 'ghost', () => { tick(); startPractice(key); });
    if (primary) primary.focus({ preventScroll: true });
  }

  function failExam(key) {
    const el = examEls.get(key);
    el.stage.hidden = true;
    clearPassline(el);
    el.result.hidden = false;
    el.result.className = 'lingo-exam__result lingo-exam__result--fail';
    el.result.innerHTML = '<h4>So close, not three yet.</h4><p>Have a quick re-read, then give it another crack.</p>';
    const primary = addBtn(el.result, 'Try again ↻', 'brass', () => { tick(); startExam(key); });
    pluck(180, 0.05, 0.32);
    if (primary) primary.focus({ preventScroll: true });
  }

  function renderPassed(key) {
    const el = examEls.get(key);
    el.dots.replaceChildren();
    el.stage.replaceChildren();
    clearPassline(el);
    el.stage.hidden = true;
    el.result.hidden = false;
    el.result.className = 'lingo-exam__result lingo-exam__result--pass';
    el.result.innerHTML = '<h4>✓ You aced this one</h4>';
    if (el.doneBtn) el.doneBtn.hidden = true;
    addContinue(el, key);
    addBtn(el.result, 'Play again for fun ↻', 'ghost', () => { tick(); startPractice(key); });
  }

  for (const [key, card] of hubCards) {
    card.addEventListener('click', () => showLesson(key));
    // a finished card wears a green tick; clicking it unticks the lesson so you can retake it
    const undo = document.createElement('button');
    undo.type = 'button';
    undo.className = 'lingo-hub__undo';
    undo.setAttribute('aria-label', `Mark ${TITLES[key] || 'this lesson'} as not done`);
    undo.title = 'Mark as not done';
    const tk = document.createElement('span');
    tk.className = 'lingo-hub__undo-tick'; tk.setAttribute('aria-hidden', 'true'); tk.textContent = '\u2713';
    const rd = document.createElement('span');
    rd.className = 'lingo-hub__undo-redo'; rd.setAttribute('aria-hidden', 'true'); rd.textContent = '\u21BA';
    undo.append(tk, rd);
    undo.addEventListener('click', (e) => { e.stopPropagation(); unmarkDone(key); });
    card.insertAdjacentElement('afterend', undo);
  }
  if (hubReset) {
    hubReset.addEventListener('click', () => {
      if (!doneLessons.size) return;
      if (!window.confirm('Reset all your Coffee School progress? This clears every tick on this device.')) return;
      doneLessons.clear();
      try { localStorage.setItem(PROGRESS_KEY, '[]'); } catch { /* ignore */ }
      if (outro) outro.hidden = true;
      if (resetQuizForm) resetQuizForm(); // clear the personality-quiz result panel too
      paintHub();
      updateBar();
      renderExam(currentLesson);
      if (hubCountEl) { hubCountEl.setAttribute('tabindex', '-1'); hubCountEl.focus({ preventScroll: true }); } // reset link just hid itself
      tick();
    });
  }
  for (const [key, a] of navLinks) {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      showLesson(key);
    });
  }
  // label every Done button with where it's taking you
  for (const [key, section] of sections) {
    const btn = section.querySelector('[data-lesson-done]');
    const next = LESSONS[LESSONS.indexOf(key) + 1];
    if (btn && next) textNode(btn).data = `Done, next: ${TITLES[next]} →`;
  }
  for (const btn of $$('[data-lesson-done]')) {
    btn.addEventListener('click', () => {
      markDone(currentLesson);
      const next = LESSONS[LESSONS.indexOf(currentLesson) + 1];
      if (next) {
        showLesson(next);
        // the clicked button just vanished with its section — park focus on
        // the new lesson's heading so keyboard/SR users aren't dropped on body
        const h2 = sections.get(next).querySelector('.lingo-h2');
        if (h2) {
          h2.setAttribute('tabindex', '-1');
          h2.focus({ preventScroll: true });
        }
      }
    });
  }
  window.addEventListener('hashchange', () => {
    const key = location.hash.slice(1);
    if (sections.has(key)) showLesson(key);
  });
  {
    const fromHash = location.hash.slice(1);
    const resume = LESSONS.find((k) => !doneLessons.has(k)) || 'the-fruit';
    // Deep links open their lesson; a plain visit resumes at the first
    // unfinished one without yanking the page away from the hero.
    const deepLinked = sections.has(fromHash);
    showLesson(deepLinked ? fromHash : resume, { scroll: deepLinked, updateUrl: deepLinked, silentSound: true });
  }

  /* ================= hero: pointer parallax on the floating pills ================= */
  const heroEl = $('[data-lingo-hero]');
  if (heroEl && !reduceMotion && window.matchMedia('(pointer: fine)').matches) {
    const heroPills = $$('.lingo-fpill', heroEl);
    const depths = heroPills.map((_, i) => 0.4 + (i % 5) * 0.18);
    let parRaf = 0;
    let lastEvent = null;
    heroEl.addEventListener('pointermove', (e) => {
      lastEvent = e;
      if (parRaf) return;
      parRaf = requestAnimationFrame(() => {
        parRaf = 0;
        const r = heroEl.getBoundingClientRect();
        const nx = (lastEvent.clientX - r.left) / r.width - 0.5;
        const ny = (lastEvent.clientY - r.top) / r.height - 0.5;
        heroPills.forEach((pill, i) => {
          pill.style.setProperty('--par-x', `${(-nx * 28 * depths[i]).toFixed(1)}px`);
          pill.style.setProperty('--par-y', `${(-ny * 20 * depths[i]).toFixed(1)}px`);
        });
      });
    });
  }

  /* ================= hero: tap a floating word for a sound ================= */
  if (heroEl) {
    for (const pill of $$('.lingo-fpill', heroEl)) {
      pill.addEventListener('click', () => {
        const clip = pill.dataset.clip; // future: a licensed / your-own voice recording
        if (clip) {
          try { const a = new Audio(clip); a.volume = 0.9; a.play(); } catch { /* ignore */ }
        } else {
          const fn = HERO_SOUNDS[pill.textContent.trim().toLowerCase()];
          (fn || (() => pluck(600)))();
        }
        pill.style.scale = '1.35';
        setTimeout(() => { pill.style.scale = ''; }, 170);
      });
    }
  }

  /* ===== hero: scatter the floating words into clear space (random each load, never under the text) ===== */
  const heroFloat = heroEl && $('.lingo-hero__float', heroEl);
  const heroInner = heroEl && $('.lingo-hero__inner', heroEl);
  if (heroFloat && heroInner) {
    const floatPills = $$('.lingo-fpill', heroFloat);
    const hit = (a, b, gap) => a.left < b.right + gap && a.right > b.left - gap && a.top < b.bottom + gap && a.bottom > b.top - gap;
    const placePills = () => {
      const hr = heroEl.getBoundingClientRect();
      const ir = heroInner.getBoundingClientRect();
      const pad = 24;
      const textBox = {
        left: ir.left - hr.left - pad, right: ir.right - hr.left + pad,
        top: ir.top - hr.top - pad, bottom: ir.bottom - hr.top + pad,
      };
      const placed = [];
      for (const pill of floatPills) {
        if (pill.offsetParent === null) continue; // hidden on this breakpoint
        const w = pill.offsetWidth, h = pill.offsetHeight;
        const maxX = Math.max(8, hr.width - w - 8);
        const maxY = Math.max(8, hr.height - h - 8);
        let ok = false;
        for (let t = 0; t < 80; t++) {
          const x = 8 + Math.random() * (maxX - 8);
          const y = 8 + Math.random() * (maxY - 8);
          const box = { left: x, right: x + w, top: y, bottom: y + h };
          if (hit(box, textBox, 0)) continue;
          if (placed.some((p) => hit(box, p, 16))) continue;
          pill.style.setProperty('--fx', Math.round(x) + 'px');
          pill.style.setProperty('--fy', Math.round(y) + 'px');
          pill.style.visibility = '';
          placed.push(box);
          ok = true;
          break;
        }
        if (!ok) pill.style.visibility = 'hidden'; // no clean spot; hide rather than sit under the text
      }
    };
    placePills();
    let rzTimer;
    window.addEventListener('resize', () => { clearTimeout(rzTimer); rzTimer = setTimeout(placePills, 200); });
  }

  /* ================= cherry anatomy ================= */
  const cherry = $('[data-lingo-cherry]');
  if (cherry) {
    const svg = $('.lingo-cherry__svg', cherry);
    const layers = $$('[data-cherry-layer]', cherry);
    const rings = $$('[data-cherry-ring]', cherry);
    const infos = $$('[data-cherry-info]', cherry);
    const tags = $$('[data-cherry-tag]', cherry);
    // plucks descend in pitch as you dig toward the seed
    const LAYER_NOTES = { skin: 740, pulp: 620, mucilage: 520, parchment: 440, seed: 350 };
    const activate = (name) => {
      pluck(LAYER_NOTES[name] || 600);
      setPressed(layers, layers.find((l) => l.dataset.cherryLayer === name));
      svg.classList.add('has-focus');
      for (const r of rings) r.classList.toggle('is-lit', r.dataset.cherryRing === name);
      for (const i of infos) i.hidden = i.dataset.cherryInfo !== name;
      // SVG elements have no .hidden property — toggle the attribute itself
      for (const t of tags) t.toggleAttribute('hidden', t.dataset.cherryTag !== name);
    };
    for (const btn of layers) {
      btn.addEventListener('click', () => activate(btn.dataset.cherryLayer));
    }
    for (const ring of rings) {
      ring.addEventListener('click', () => activate(ring.dataset.cherryRing));
    }
  }

  /* ================= wine glasses ================= */
  const wine = $('[data-lingo-wine]');
  if (wine) {
    const glasses = $$('[data-wine]', wine);
    const panels = $$('[data-wine-panel]', wine);
    const marker = $('[data-wine-marker]', wine);
    const positions = { white: '7%', rose: '50%', red: '93%' };
    const GLASS_NOTES = { white: 1568, rose: 1318, red: 1046 }; // higher = lighter wine
    const pick = (name, opts = {}) => {
      const glass = glasses.find((g) => g.dataset.wine === name);
      if (!opts.silent) {
        ting(GLASS_NOTES[name]);
        if (glass) {
          glass.classList.remove('is-clink');
          void glass.offsetWidth;
          glass.classList.add('is-clink');
          setTimeout(() => glass.classList.remove('is-clink'), 550);
        }
      }
      setPressed(glasses, glass);
      for (const p of panels) p.hidden = p.dataset.winePanel !== name;
      if (marker) marker.style.setProperty('--marker', positions[name] || '50%');
    };
    for (const g of glasses) g.addEventListener('click', () => pick(g.dataset.wine));
    pick('red', { silent: true });

    // The spectrum bar doubles as a slider: drag or tap, snaps to the
    // nearest of the three styles. Pointer-only enhancement — the glass
    // buttons remain the keyboard/screen-reader path.
    const spectrum = $('.lingo-wine__spectrum', wine);
    if (spectrum && marker) {
      const stops = [['white', 0.07], ['rose', 0.5], ['red', 0.93]];
      let draggingWine = false;
      const ratioFrom = (x) => {
        const r = spectrum.getBoundingClientRect();
        return Math.min(1, Math.max(0, (x - r.left) / r.width));
      };
      const nearest = (ratio) =>
        stops.reduce((best, s) => (Math.abs(ratio - s[1]) < Math.abs(ratio - best[1]) ? s : best), stops[0]);
      spectrum.addEventListener('pointerdown', (e) => {
        draggingWine = true;
        spectrum.classList.add('is-dragging');
        spectrum.setPointerCapture(e.pointerId);
        marker.style.setProperty('--marker', `${ratioFrom(e.clientX) * 100}%`);
      });
      spectrum.addEventListener('pointermove', (e) => {
        if (!draggingWine) return;
        marker.style.setProperty('--marker', `${ratioFrom(e.clientX) * 100}%`);
      });
      const settle = (e) => {
        if (!draggingWine) return;
        draggingWine = false;
        spectrum.classList.remove('is-dragging');
        pick(nearest(ratioFrom(e.clientX))[0]);
      };
      spectrum.addEventListener('pointerup', settle);
      spectrum.addEventListener('pointercancel', settle);
    }
  }

  /* ================= variety family tree ================= */
  const tree = $('[data-lingo-tree]');
  const treeDetail = $('[data-variety-detail]');
  if (tree && treeDetail) {
    const chips = $$('[data-variety]', tree);
    const panels = $$('[data-variety-panel]', treeDetail);
    for (const chip of chips) {
      chip.addEventListener('click', () => {
        pluck(700, 0.05);
        setPressed(chips, chip);
        for (const p of panels) p.hidden = p.dataset.varietyPanel !== chip.dataset.variety;
      });
    }
  }

  /* ================= step 1: balance sliders ================= */
  const balance = $('[data-lingo-balance]');
  if (balance) {
    const LEVELS = ['low', 'low-medium', 'medium', 'medium-high', 'high'];
    const inputs = $$('[data-balance-input]', balance);
    const valNodes = new Map(
      $$('[data-balance-val]', balance).map((el) => [el.dataset.balanceVal, textNode(el)])
    );
    const verdictEl = $('[data-balance-verdict]', balance);
    const verdictNode = textNode(verdictEl);
    let wasBalanced = true;
    const update = () => {
      const v = {};
      for (const input of inputs) {
        const key = input.dataset.balanceInput;
        v[key] = Number(input.value);
        input.setAttribute('aria-valuetext', LEVELS[v[key]]);
        const node = valNodes.get(key);
        if (node) node.data = LEVELS[v[key]];
      }
      const spread = Math.max(v.sweet, v.acid, v.bitter) - Math.min(v.sweet, v.acid, v.bitter);
      let verdict;
      if (v.sweet === 0 && v.bitter >= 3) {
        verdict = 'All bitterness, no sweetness. Lopsided. (A splash of milk would rescue this one.)';
      } else if (v.acid >= 3 && v.sweet <= 1) {
        verdict = 'Sharp and lean. Acidity is elbowing everyone else off stage.';
      } else if (spread <= 1) {
        verdict = 'Balanced. The three voices are singing together.';
      } else if (v.acid >= 3 && v.sweet >= 2) {
        verdict = 'Bright but balanced. High acid with enough sweetness holding its hand. Kenyan energy.';
      } else if (v.bitter >= 3 && v.sweet >= 2) {
        verdict = 'Bold and dark but held together by sweetness. Classic espresso-roast profile.';
      } else if (v.sweet >= 3 && v.bitter <= 1 && v.acid <= 1) {
        verdict = 'Sweet and mellow. Dessert in a cup, if a little sleepy.';
      } else {
        verdict = 'A bit lopsided. One voice is drowning out the band.';
      }
      verdictNode.data = verdict;
      // little happy bounce when the cup comes into balance
      const isBalanced = verdict.startsWith('Balanced') || verdict.includes('but balanced') || verdict.includes('held together');
      if (isBalanced && !wasBalanced) {
        verdictEl.classList.remove('is-happy');
        void verdictEl.offsetWidth;
        verdictEl.classList.add('is-happy');
        setTimeout(() => verdictEl.classList.remove('is-happy'), 500);
      }
      wasBalanced = isBalanced;
    };
    for (const input of inputs) {
      input.addEventListener('input', () => {
        blip(560 + Number(input.value) * 55, 0.018, 0.06);
        update();
      });
    }
    update();
  }

  /* ================= step 2: broad -> narrow flavour ================= */
  const flavour = $('[data-lingo-flavour]');
  if (flavour) {
    const SUBS = {
      fruity: ['berry', 'citrus', 'stone fruit', 'tropical', 'dried fruit'],
      chocolatey: ['milk chocolate', 'dark chocolate', 'cocoa', 'mocha'],
      nutty: ['roasted almond', 'hazelnut', 'peanut', 'walnut'],
      floral: ['jasmine', 'honeysuckle', 'rose', 'orange blossom'],
      spicy: ['cinnamon', 'clove', 'nutmeg', 'black pepper'],
    };
    const NARROWEST = {
      berry: 'blackcurrant', citrus: 'ruby grapefruit', 'stone fruit': 'ripe apricot',
      tropical: 'passionfruit', 'dried fruit': 'raisin',
      'milk chocolate': 'milk chocolate', 'dark chocolate': '70% dark chocolate', cocoa: 'Dutch cocoa', mocha: 'mocha',
      'roasted almond': 'roasted almond', hazelnut: 'hazelnut praline', peanut: 'peanut brittle', walnut: 'walnut',
      jasmine: 'jasmine', honeysuckle: 'honeysuckle', rose: 'Turkish delight', 'orange blossom': 'orange blossom',
      cinnamon: 'cinnamon scroll', clove: 'clove', nutmeg: 'nutmeg', 'black pepper': 'cracked pepper',
    };
    const familyChips = $$('[data-flavour-family]', flavour);
    const narrowWrap = $('[data-flavour-narrow]', flavour);
    const subWrap = $('[data-flavour-subwrap]', flavour);
    const result = $('[data-flavour-result]', flavour);

    // Pre-build one chip row per family, once, at init.
    const subGroups = {};
    for (const [family, subs] of Object.entries(SUBS)) {
      const group = document.createElement('div');
      group.className = 'lingo-chips lingo-chips--select';
      group.hidden = true;
      for (const s of subs) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lingo-chip lingo-chip--sm';
        b.setAttribute('aria-pressed', 'false');
        b.textContent = s;
        b.addEventListener('click', () => {
          chime();
          setPressed($$('button', group), b);
          result.hidden = false;
          textNode(result).data = `“${NARROWEST[s] || s}”, that’s a real tasting note. You’re doing it.`;
        });
        group.appendChild(b);
      }
      subGroups[family] = group;
      subWrap.appendChild(group);
    }
    for (const chip of familyChips) {
      chip.addEventListener('click', () => {
        tick();
        setPressed(familyChips, chip);
        narrowWrap.hidden = false;
        result.hidden = true;
        for (const [family, group] of Object.entries(subGroups)) {
          group.hidden = family !== chip.dataset.flavourFamily;
        }
      });
    }
  }

  /* ================= step 3: texture words ================= */
  const tactile = $('[data-lingo-tactile]');
  if (tactile) {
    const FEELS = {
      silky: 'Smooth and fine, slips over the tongue like satin.',
      creamy: 'Rounded and plush, like milk even when there’s none in it.',
      syrupy: 'Thick, coating, slow-moving. Maple-syrup energy.',
      juicy: 'Light and mouth-watering, like biting into fruit.',
      oily: 'Slick, with a buttery slipperiness. Common in darker roasts.',
      thin: 'Lacks substance, the coffee equivalent of weak cordial.',
      grainy: 'A faint fine-sediment feel, like the last sip of hot chocolate.',
    };
    const chips = $$('[data-texture]', tactile);
    const outNode = textNode($('[data-texture-out]', tactile));
    for (const chip of chips) {
      chip.addEventListener('click', () => {
        tick();
        setPressed(chips, chip);
        outNode.data = FEELS[chip.dataset.texture] || '';
      });
    }
  }

  /* ================= step 4: WBC scale ================= */
  const scale = $('[data-lingo-scale]');
  if (scale) {
    const WORDS = [
      'Unacceptable. This should not have been served. Fired. (Kidding. Mostly.)',
      'Acceptable. A couple of redeeming features, but only a couple.',
      'Average. Any old coffee shop on any old day.',
      'Good. You’d happily order it again tomorrow.',
      'Very good. You’d tell a friend which café made it.',
      'Excellent. You’d ask the barista what it is and buy a bag.',
      'Extraordinary. Everything harmonious, complex and clear. Judges hand these out about never.',
    ];
    const buttons = $$('[data-scale]', scale);
    const outNode = textNode($('[data-scale-out]'));
    for (const b of buttons) {
      b.addEventListener('click', () => {
        const n = Number(b.dataset.scale);
        blip(294 * Math.pow(2, n / 6), 0.04, 0.2); // pitch climbs with the score
        setPressed(buttons, b);
        outNode.data = WORDS[n] || '';
      });
    }
  }

  /* ================= roast slider ================= */
  const roast = $('[data-lingo-roast]');
  if (roast) {
    const input = $('[data-roast-input]', roast);
    const bean = $('[data-roast-bean]', roast);
    const labelNode = textNode($('[data-roast-label]', roast));
    const meters = {
      roast: $('[data-meter="roast"]', roast),
      origin: $('[data-meter="origin"]', roast),
      caffeine: $('[data-meter="caffeine"]', roast),
    };
    const STAGES = [
      [15, 'Light', '#c49a6c'],
      [38, 'Medium', '#a9744a'],
      [62, 'Medium-dark', '#7d4e2a'],
      [82, 'Dark', '#4e2c15'],
      [101, 'Very dark (oily)', '#2b170b'],
    ];
    let lastStage = null;
    const update = () => {
      const v = Number(input.value);
      const stage = STAGES.find(([max]) => v < max) || STAGES[STAGES.length - 1];
      if (lastStage && stage[1] !== lastStage) {
        crackle(3 + Math.round(v / 30)); // more pops the darker you go
        bean.classList.remove('is-crack');
        void bean.offsetWidth;
        bean.classList.add('is-crack');
        setTimeout(() => bean.classList.remove('is-crack'), 400);
      }
      lastStage = stage[1];
      input.setAttribute('aria-valuetext', stage[1]);
      labelNode.data = stage[1];
      bean.style.setProperty('--bean', stage[2]);
      meters.roast.style.setProperty('--fill', `${8 + v * 0.9}%`);
      meters.origin.style.setProperty('--fill', `${96 - v * 0.82}%`);
      meters.caffeine.style.setProperty('--fill', `${96 - v * 0.05}%`);
    };
    input.addEventListener('input', update);
    update();
  }

  /* ================= freshness: roast-style toggle ================= */
  const freshToggle = $('[data-fresh-toggle]');
  if (freshToggle) {
    const chips = $$('[data-fresh-style]', freshToggle);
    const timelines = $$('[data-fresh-timeline]');
    for (const chip of chips) {
      chip.addEventListener('click', () => {
        tick();
        setPressed(chips, chip);
        for (const t of timelines) t.hidden = t.dataset.freshTimeline !== chip.dataset.freshStyle;
      });
    }
  }

  /* ================= dial-in lab ================= */
  const dialin = $('[data-lingo-dialin]');
  if (dialin) {
    // [direction label, meter values (base recipe = 5), verdict]
    const DIAL = {
      grind: {
        a: ['Finer', { acidity: 4, flavour: 7, aftertaste: 7, body: 6.5 }, 'Finer grind = water fights harder through the puck (the compressed disc of coffee in the basket), so more gets extracted. Expect more punch and body, push too far and it turns bitter and drying.'],
        b: ['Coarser', { acidity: 6.5, flavour: 4, aftertaste: 4, body: 4 }, 'Coarser grind = faster flow, lighter extraction. Brighter and juicier, too far and it goes sour, salty and thin.'],
      },
      dose: {
        a: ['More coffee', { acidity: 5.5, flavour: 6.5, aftertaste: 6, body: 7 }, 'A bigger dose with the same water = more concentration and body, but the puck extracts less evenly, so sharpness creeps in if you push it.'],
        b: ['Less coffee', { acidity: 4.5, flavour: 4, aftertaste: 4.5, body: 3.5 }, 'A smaller dose extracts more thoroughly, softer and lighter, but easy to tip into thin and over-extracted.'],
      },
      yield: {
        a: ['Shorter (ristretto-ish)', { acidity: 6, flavour: 7, aftertaste: 6, body: 7 }, 'Cutting the shot short concentrates everything, syrupy and intense, and a little sharper, because the mellow late stage never lands in the cup.'],
        b: ['Longer (lungo-ish)', { acidity: 4.5, flavour: 4.5, aftertaste: 5.5, body: 3.5 }, 'Letting it run dilutes the shot, gentler and thinner with more total extraction: lovely clarity first, bitterness if you keep going.'],
      },
      temp: {
        a: ['Hotter', { acidity: 4, flavour: 6, aftertaste: 6, body: 6 }, 'Hotter water extracts harder, rounder and sweeter at first, then roasty bitterness muscles in.'],
        b: ['Cooler', { acidity: 6.5, flavour: 4.5, aftertaste: 4, body: 4.5 }, 'Cooler water holds back extraction, brighter and lighter, drifting sour if you go too cool.'],
      },
      pressure: {
        a: ['Higher', { acidity: 4.5, flavour: 5.5, aftertaste: 5, body: 6 }, 'More pressure squeezes out more body, but past the classic 9 bar you mostly buy spurty, uneven shots. (That unevenness has a name, next lesson.)'],
        b: ['Lower', { acidity: 5.5, flavour: 5.5, aftertaste: 5, body: 4.5 }, 'Gentler pressure slows everything down, softer, almost tea-like shots. Very fashionable lately.'],
      },
    };
    const varChips = $$('[data-dial-var]', dialin);
    const dirBtns = $$('[data-dial-dir]', dialin);
    const dirNodes = dirBtns.map((b) => textNode(b));
    const dialMeters = {};
    for (const m of $$('[data-dial-meter]', dialin)) dialMeters[m.dataset.dialMeter] = m;
    const dialVerdict = textNode($('[data-dial-verdict]', dialin));
    let curVar = 'grind';
    let curDir = 'a';
    const paintDial = () => {
      const [, values, verdict] = DIAL[curVar][curDir];
      dirNodes[0].data = DIAL[curVar].a[0];
      dirNodes[1].data = DIAL[curVar].b[0];
      for (const [k, el] of Object.entries(dialMeters)) el.style.setProperty('--fill', `${values[k] * 10}%`);
      dialVerdict.data = verdict;
    };
    for (const chip of varChips) {
      chip.addEventListener('click', () => {
        tick();
        curVar = chip.dataset.dialVar;
        setPressed(varChips, chip);
        paintDial();
      });
    }
    for (const btn of dirBtns) {
      btn.addEventListener('click', () => {
        flick();
        curDir = btn.dataset.dialDir;
        setPressed(dirBtns, btn);
        paintDial();
      });
    }
    paintDial();
  }

  /* ================= milk school ================= */
  const milk = $('[data-lingo-milk]');
  if (milk) {
    const ZONES = [
      [5, 'Dairy storage', 'Fridge country: keep dairy at 1–5°C. Cold milk stretches into better microfoam.'],
      [20, 'Alt-milk storage', 'Unopened cartons live happily here. Once opened, into the fridge, same as dairy.'],
      [40, 'No-man’s land', 'Too warm to store, too cold to serve. Nothing good lives here.'],
      [50, 'Babyccino zone', 'Mildly warm, minimal sweetness, built for the kiddos.'],
      [55, 'Alt-milk sweet spot', 'Most alt milks hit their stride around 55 to 60, a touch cooler than dairy. Almond is the fussiest and likes it coolest, while oat and soy will take a bit more heat, though it really depends on the brand and its stabilisers.'],
      [65, 'Dairy sweet spot', 'Lactose sweetness peaks right here and the microfoam goes silky. This is flat-white country.'],
      [70, 'Extra hot', 'Sweetness fading, texture coarsening, but your cup, your call. (Travel mug? We get it.)'],
      [Infinity, 'Too hot!', 'Scalded milk, burnt tongue, sad barista. Nobody wins past 70°C.'],
    ];
    const input = $('[data-milk-input]', milk);
    const tempNode = textNode($('[data-milk-temp]', milk));
    const zoneNode = textNode($('[data-milk-zone]', milk));
    const descNode = textNode($('[data-milk-desc]', milk));
    const milkMarker = $('[data-milk-marker]', milk);
    let lastZone = null;
    const updateMilk = () => {
      const t = Number(input.value);
      const zone = ZONES.find(([max]) => t < max) || ZONES[ZONES.length - 1];
      tempNode.data = String(t);
      zoneNode.data = zone[1];
      descNode.data = zone[2];
      input.setAttribute('aria-valuetext', `${t} degrees, ${zone[1]}`);
      if (milkMarker) {
        milkMarker.style.setProperty('--pos', `${(t / 75) * 100}%`);
        milkMarker.classList.toggle('is-steamy', t >= 50 && t < 70);
      }
      if (lastZone && zone[1] !== lastZone) {
        if (zone[1] === 'Dairy sweet spot') ting(1568);
        else steam(t / 75);
      }
      lastZone = zone[1];
    };
    input.addEventListener('input', updateMilk);
    updateMilk();
  }

  /* ================= geisha poll ================= */
  const poll = $('[data-lingo-poll]');
  if (poll) {
    const RESPONSES = {
      yes: 'Correct answer. We’ll pretend the mortgage didn’t hear that.',
      once: 'The tasting-flight spirit, exactly how we’d do it too.',
      no: 'Fair call. The $6 flat white remains one of life’s great bargains.',
    };
    const pollChips = $$('[data-poll]', poll);
    const pollOut = $('[data-poll-out]', poll);
    for (const chip of pollChips) {
      chip.addEventListener('click', () => {
        tick();
        setPressed(pollChips, chip);
        pollOut.hidden = false;
        textNode(pollOut).data = RESPONSES[chip.dataset.poll] || '';
      });
    }
  }

  /* ================= taste: the nose (retronasal) test ================= */
  const nose = $('[data-lingo-nose]');
  if (nose) {
    const btn = $('[data-nose-toggle]', nose);
    const out = $('[data-nose-out]', nose);
    const outNode = textNode(out);
    const btnNode = textNode(btn);
    let pinched = false;
    btn.addEventListener('click', () => {
      pinched = !pinched;
      btn.setAttribute('aria-pressed', pinched ? 'true' : 'false');
      out.hidden = false;
      if (pinched) {
        swish();
        btnNode.data = 'Now let go →';
        outNode.data = 'Nose pinched: just sweet and a dull warmth. The blueberry, the caramel, the jasmine, all gone. That’s your tongue working alone.';
      } else {
        ting(1568);
        btnNode.data = 'Pinch your nose & sip →';
        outNode.data = 'And… flavour floods back in. That whoosh is retronasal smell, 80% of the experience, arriving through the back of your nose. Your tongue never left; your nose did all the talking.';
      }
    });
  }

  /* ================= lesson 8: fault diagnoser ================= */
  const diag = $('[data-lingo-diag]');
  if (diag) {
    const FAULTS = {
      over: ['Over-extracted', 'Water pulled too much out, grind too fine, water too hot, or shot run too long. Batch brew left sitting for hours goes here too.', 'Coarser grind, cooler water, or a shorter shot. Bin batch brew after ~45–60 min.'],
      under: ['Under-extracted', 'Water didn’t pull enough, grind too coarse, water too cool, or the shot ran too fast.', 'Finer grind, hotter water, or a longer shot. Sour usually means “give it more”.'],
      stewed: ['Stewed / over-held', 'Brewed coffee that sat too long on heat, the flavours have merged into a flat, murky sludge.', 'Not you, not the bean, it’s old brew. Ask for a fresh batch; brew smaller and more often.'],
      stale: ['Stale beans', 'Beans well past their best, the acidity and life have faded, leaving a flat, cardboardy cup. Often an over-full hopper left out for weeks.', 'Check the roast date, buy smaller and fresher, and keep beans sealed and cool.'],
      channel: ['Channelling', 'Water found a crack in the puck and rushed through, so part over-extracts (bitter) and part under-extracts (sour) at once, with a drying edge.', 'A prep problem, not a recipe one: distribute and tamp evenly. Not your fault as a drinker!'],
      cardboard: ['Old-crop / faded green', 'That papery, wet-cardboard aftertaste means the green coffee itself was old before it was even roasted.', 'On us as the roaster to source fresh green, a good sign to try a different, fresher coffee.'],
      mould: ['Medicinal taint', 'Band-Aid, hospital, medicinal, rubbery. A genuine defect, usually a phenolic taint from over-fermentation, sometimes a mould that slipped through.', 'Genuinely faulty, don’t drink it. Tell whoever served it; a good roaster wants to know.'],
    };
    const chips = $$('[data-fault]', diag);
    const card = $('[data-diag-card]', diag);
    const nameNode = textNode($('[data-diag-name]', diag));
    const causeNode = textNode($('[data-diag-cause]', diag));
    const fixNode = textNode($('[data-diag-fix]', diag));
    for (const chip of chips) {
      chip.addEventListener('click', () => {
        tick();
        setPressed(chips, chip);
        const f = FAULTS[chip.dataset.fault];
        if (!f) return;
        card.hidden = false;
        nameNode.data = f[0];
        causeNode.data = f[1];
        fixNode.data = f[2];
      });
    }
  }

  /* ================= details expanders: a soft swish ================= */
  for (const det of $$('.lingo-details')) {
    det.addEventListener('toggle', () => swish());
  }

  /* ================= glossary: flip =================
     The lesson-5 slim deck ([data-lingo-gloss]) has no search box, chips or
     empty banner of its own, so it only needs flip handlers here. Dictionary
     search/filter/empty is owned solely by coffee-glossary.js (scoped to
     [data-coffee-glossary]). The old search/filter code in this block queried
     [data-gloss-search]/[data-gloss-empty] unscoped, so it hijacked the
     dictionary's controls and fought coffee-glossary.js over the "No lingo
     found" banner. Removed. */
  const gloss = $('[data-lingo-gloss]');
  if (gloss) {
    const cards = $$('.lingo-gcard', gloss);
    for (const card of cards) {
      const btn = $('.lingo-gcard__btn', card);
      const front = $('.lingo-gcard__front', card);
      const back = $('.lingo-gcard__back', card);
      // Only the visible face is exposed to screen readers, otherwise the
      // button's name is the term AND its full definition at once.
      const face = (flipped) => {
        if (front) front.setAttribute('aria-hidden', flipped ? 'true' : 'false');
        if (back) back.setAttribute('aria-hidden', flipped ? 'false' : 'true');
      };
      face(false);
      btn.addEventListener('click', () => {
        swish();
        const flipped = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', flipped ? 'true' : 'false');
        face(flipped);
      });
    }
  }

  /* ================= quiz ================= */
  const quiz = $('[data-lingo-quiz]');
  if (quiz) {
    const questions = $$('[data-quiz-q]', quiz);
    const resultWrap = $('[data-quiz-result]', quiz);
    const resultPanels = $$('[data-quiz-panel]', quiz);
    const scores = { natural: 0, washed: 0, honey: 0, wild: 0, milk: 0 };
    let answered = 0;

    const finish = () => {
      // Wild is a lifestyle: any strong wild signal wins outright. Otherwise a
      // dead heat shows BOTH archetypes honestly instead of silently picking one.
      let winners = [];
      if (scores.wild >= 3) {
        winners = ['wild'];
      } else {
        const order = ['natural', 'washed', 'honey', 'milk'];
        const top = Math.max(...order.map((k) => scores[k]));
        winners = order.filter((k) => scores[k] === top && top > 0);
        if (!winners.length) winners = ['natural'];
        if (winners.length > 2) winners = winners.slice(0, 2);
      }
      for (const p of resultPanels) p.hidden = !winners.includes(p.dataset.quizPanel);
      const tieNote = $('[data-quiz-tie]', quiz);
      if (tieNote) tieNote.hidden = winners.length < 2;
      resultWrap.hidden = false;
      if (outro) outro.hidden = false;
      markDone('quiz', { silent: true }); // finish() plays its own celebration
      fanfare();
      confetti();
      resultWrap.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
    };

    for (const [qi, q] of questions.entries()) {
      const options = $$('[data-quiz-a]', q);
      for (const opt of options) {
        opt.addEventListener('click', () => {
          if (q.dataset.done) return; // one answer per question
          q.dataset.done = '1';
          pluck(720, 0.05);
          setPressed(options, opt);
          for (const pair of opt.dataset.quizA.split(',')) {
            const [key, w] = pair.split(':');
            if (key in scores) scores[key] += Number(w) || 1;
          }
          answered += 1;
          const next = questions[qi + 1];
          if (next) {
            next.hidden = false;
            next.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
          }
          if (answered === questions.length) finish();
        });
      }
    }

    // Pure DOM reset of the personality quiz, reused by the hub untick/reset controls.
    resetQuizForm = () => {
      for (const key of Object.keys(scores)) scores[key] = 0;
      answered = 0;
      resultWrap.hidden = true;
      for (const [qi, q] of questions.entries()) {
        delete q.dataset.done;
        q.hidden = qi !== 0;
        setPressed($$('[data-quiz-a]', q), null);
      }
    };
    const reset = $('[data-quiz-reset]', quiz);
    if (reset) {
      reset.addEventListener('click', () => {
        tick();
        resetQuizForm();
        questions[0].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        // The reset button just hid itself with the result wrap — park focus
        // on question 1 so keyboard/SR users don't fall back to the page top.
        const firstOption = $('[data-quiz-a]', questions[0]);
        if (firstOption) firstOption.focus();
      });
    }
  }
})();
